import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DeviceFrame } from '../shell/DeviceFrame';
import { ChallanListingScreen } from '../screens/ChallanListingScreen';
import { PaymentSuccessScreen } from '../screens/PaymentSuccessScreen';
import { FastagRechargeScreen } from '../screens/FastagRechargeScreen';
import { Button } from '../design-system/Button';
import { Text } from '../design-system/Text';
import { usePrototypeStore } from '../../stores/prototypeStore';
import { useSessionStore } from '../../stores/sessionStore';
import { analytics } from '../../lib/analytics';
import { ArrowLeft, Play, Pause, SkipBack, SkipForward, RotateCcw } from 'lucide-react';

const STEPS = [
  {
    title: 'Unqualified user',
    description: 'User has sufficient FASTag balance. No nudges appear.',
    config: { variant: 'recommended', state: 'unqualified', list: 'long' },
    screen: 'listing' as const,
  },
  {
    title: 'Low balance',
    description: 'Header chip + inline contextual card appear between challans.',
    config: { variant: 'recommended', state: 'low', list: 'long' },
    screen: 'listing' as const,
  },
  {
    title: 'Negative balance',
    description: 'Urgent sticky banner joins the header chip.',
    config: { variant: 'recommended', state: 'negative', list: 'long' },
    screen: 'listing' as const,
  },
  {
    title: 'User dismisses the nudge',
    description: 'Sticky banner is dismissed while header chip stays.',
    config: { variant: 'recommended', state: 'negative', list: 'long' },
    screen: 'listing' as const,
    dismiss: 'nudge_banner',
  },
  {
    title: 'User recharges FASTag',
    description: 'User taps Recharge and completes the top-up.',
    config: { variant: 'recommended', state: 'negative', list: 'long' },
    screen: 'recharge' as const,
  },
  {
    title: 'User pays challans',
    description: 'After recharge, the user returns and proceeds to payment.',
    config: { variant: 'recommended', state: 'unqualified', list: 'long' },
    screen: 'success' as const,
  },
  {
    title: 'Post-payment experience',
    description: 'A celebratory FASTag card appears after payment success.',
    config: { variant: 'recommended', state: 'low', list: 'long' },
    screen: 'success' as const,
  },
];

export function DemoModeScreen() {
  const navigate = useNavigate();
  const prototype = usePrototypeStore();
  const session = useSessionStore();
  const [step, setStep] = useState(1);
  const [autoPlay, setAutoPlay] = useState(true);

  const applyStep = (nextStep: number) => {
    const config = STEPS[nextStep - 1];
    prototype.setActiveVariant(config.config.variant as any);
    prototype.setQualificationState(config.config.state as any);
    prototype.setListLength(config.config.list as any);
    prototype.setBalanceValue(null);
    session.resetDismissals();
    session.resetRecharge();

    if (config.dismiss) {
      session.dismissNudge(config.dismiss);
    }
    if (config.screen === 'recharge') {
      session.startRecharge('demo');
    }
    if (config.screen === 'success') {
      session.completePayment();
    }
    if (nextStep === 6) {
      session.completeRecharge();
    }
  };

  useEffect(() => {
    analytics.emit('demo_step', { step });
    applyStep(step);
  }, [step]);

  useEffect(() => {
    if (!autoPlay) return;
    const timer = setInterval(() => {
      setStep((s) => (s < STEPS.length ? s + 1 : 1));
    }, 4000);
    return () => clearInterval(timer);
  }, [autoPlay]);

  // Supply URL params to the mocked recharge screen during demo step 5
  useEffect(() => {
    if (STEPS[step - 1].screen === 'recharge') {
      const params = new URLSearchParams({
        source: 'demo',
        returnTo: '/demo',
        balance: '-32',
        recommended_amount: '300',
      });
      navigate(`/demo?${params.toString()}`, { replace: true });
    }
  }, [step, navigate]);

  const screen = STEPS[step - 1].screen;

  return (
    <div className="min-h-screen bg-slate-100 p-6 dark:bg-slate-950">
      <div className="mx-auto max-w-5xl">
        <header className="mb-6 flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/')}>
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
          <div>
            <Text variant="display-medium">Demo Mode</Text>
            <Text variant="body" color="secondary">
              7-step guided story of the FASTag nudge flow.
            </Text>
          </div>
        </header>

        <div className="mb-4 flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
          <div className="flex items-center gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-park-100 text-park-700 dark:bg-park-900/30 dark:text-park-300">
              <span className="text-sm font-bold">{step}/{STEPS.length}</span>
            </div>
            <div>
              <Text variant="title">{STEPS[step - 1].title}</Text>
              <Text variant="body" color="secondary">{STEPS[step - 1].description}</Text>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              aria-label="Previous step"
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={step === 1}
            >
              <SkipBack className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setAutoPlay((a) => !a)}
              aria-label={autoPlay ? 'Pause demo' : 'Play demo'}
            >
              {autoPlay ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              {autoPlay ? 'Pause' : 'Play'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              aria-label="Next step"
              onClick={() => setStep((s) => Math.min(STEPS.length, s + 1))}
              disabled={step === STEPS.length}
            >
              <SkipForward className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setStep(1)}>
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="flex justify-center">
          <DeviceFrame device={prototype.deviceFrame} theme={prototype.theme}>
            {screen === 'listing' && <ChallanListingScreen />}
            {screen === 'recharge' && <FastagRechargeScreen />}
            {screen === 'success' && <PaymentSuccessScreen />}
          </DeviceFrame>
        </div>
      </div>
    </div>
  );
}
